import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { mensagemDoConsole } from "../../nucleo/console";
import { ROTULO_DO_ESTADO, type EstadoDoTicket } from "../../nucleo/tickets";
import {
  ROTULO_DO_PAPEL_DA_RELACAO,
  useClonar,
  useDesfazerRelacao,
  useDesmesclar,
  useMesclar,
  useRelacionar,
  useRelacoes,
  type PapelDaRelacao,
  type ResumoDeChamado,
} from "../../nucleo/tickets-avancado";
import { Aviso, Botao, Campo, Cartao, Chip } from "../ui";

function LinkDoChamado({ chamado }: { chamado: ResumoDeChamado }) {
  return (
    <Link to={`/admin/chamados/${chamado.id}`}>
      #{chamado.numero} · {chamado.assunto}
    </Link>
  );
}

function Linha({ chamado, aoDesfazer, rotuloDoBotao }: { chamado: ResumoDeChamado; aoDesfazer?: () => void; rotuloDoBotao?: string }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-2">
      <span>
        <LinkDoChamado chamado={chamado} />{" "}
        <Chip estado="neutro">{ROTULO_DO_ESTADO[chamado.estado as EstadoDoTicket] ?? chamado.estado}</Chip>
      </span>
      {aoDesfazer ? (
        <Botao variante="fantasma" tamanho="pequeno" aria-label={rotuloDoBotao} onClick={aoDesfazer}>
          Desfazer
        </Botao>
      ) : null}
    </li>
  );
}

/** Pai, filhos, relacionados, mescla e clone de um chamado — só para quem gerencia. */
export function RelacoesDoChamado({ ticketId }: { ticketId: string }) {
  const relacoes = useRelacoes(ticketId);
  const relacionar = useRelacionar(ticketId);
  const desfazer = useDesfazerRelacao(ticketId);
  const mesclar = useMesclar(ticketId);
  const desmesclar = useDesmesclar(ticketId);
  const clonar = useClonar(ticketId);
  const navegar = useNavigate();
  const [papel, setPapel] = useState<PapelDaRelacao>("relacionado");
  const [numero, setNumero] = useState("");
  const [paraMesclar, setParaMesclar] = useState("");
  const [confirmando, setConfirmando] = useState(false);

  const falha = relacionar.error ?? desfazer.error ?? mesclar.error ?? desmesclar.error ?? clonar.error;

  function incluir(evento: FormEvent) {
    evento.preventDefault();
    const n = Number(numero);
    if (!Number.isInteger(n) || n < 1) return;
    relacionar.mutate({ numero: n, papel }, { onSuccess: () => setNumero("") });
  }

  function confirmarMescla() {
    mesclar.mutate(Number(paraMesclar), {
      onSuccess: () => {
        setParaMesclar("");
        setConfirmando(false);
      },
      onError: () => setConfirmando(false),
    });
  }

  return (
    <Cartao
      titulo="Relações, mescla e cópia"
      className="mb-4"
      data-testid="relacoes-do-chamado"
      acessorio={
        <Botao
          variante="fantasma"
          tamanho="pequeno"
          disabled={clonar.isPending}
          onClick={() => clonar.mutate(undefined, { onSuccess: (copia) => navegar(`/admin/chamados/${copia.id}`) })}
        >
          {clonar.isPending ? "Clonando…" : "Clonar chamado"}
        </Botao>
      }
    >
      {relacoes.isPending ? <p role="status">Carregando relações…</p> : null}
      {relacoes.isError ? <Aviso tom="erro">{mensagemDoConsole(relacoes.error)}</Aviso> : null}
      {relacoes.data ? (
        <>
          {relacoes.data.mesclado_em ? (
            <Aviso tom="atencao" className="mb-3">
              Este chamado foi mesclado no chamado <LinkDoChamado chamado={relacoes.data.mesclado_em} />.{" "}
              <Botao variante="fantasma" tamanho="pequeno" disabled={desmesclar.isPending} onClick={() => desmesclar.mutate()}>
                Desfazer mescla
              </Botao>
            </Aviso>
          ) : null}
          <h4 className="campo__rotulo">Chamado pai</h4>
          {relacoes.data.pai ? (
            <ul className="mb-3">
              <Linha chamado={relacoes.data.pai.chamado} rotuloDoBotao="Desfazer vínculo com o chamado pai" aoDesfazer={() => desfazer.mutate(relacoes.data.pai!.relacao_id)} />
            </ul>
          ) : (
            <p className="nota-rodape mb-3">Sem chamado pai.</p>
          )}
          <h4 className="campo__rotulo">Chamados filhos</h4>
          {relacoes.data.filhos.length ? (
            <ul className="mb-3">
              {relacoes.data.filhos.map((item) => (
                <Linha key={item.relacao_id} chamado={item.chamado} rotuloDoBotao={`Desfazer vínculo com o filho #${item.chamado.numero}`} aoDesfazer={() => desfazer.mutate(item.relacao_id)} />
              ))}
            </ul>
          ) : (
            <p className="nota-rodape mb-3">Nenhum chamado filho.</p>
          )}
          <h4 className="campo__rotulo">Relacionados</h4>
          {relacoes.data.relacionados.length ? (
            <ul className="mb-3">
              {relacoes.data.relacionados.map((item) => (
                <Linha key={item.relacao_id} chamado={item.chamado} rotuloDoBotao={`Desfazer relação com #${item.chamado.numero}`} aoDesfazer={() => desfazer.mutate(item.relacao_id)} />
              ))}
            </ul>
          ) : (
            <p className="nota-rodape mb-3">Nenhum chamado relacionado.</p>
          )}
          {relacoes.data.mesclados.length ? (
            <>
              <h4 className="campo__rotulo">Mesclados neste chamado</h4>
              <ul className="mb-3">
                {relacoes.data.mesclados.map((item) => (
                  <Linha key={item.id} chamado={item} />
                ))}
              </ul>
            </>
          ) : null}
        </>
      ) : null}

      <form className="flex flex-wrap items-end gap-3 mb-3" onSubmit={incluir} aria-label="Relacionar chamado">
        <div className="campo">
          <label className="campo__rotulo" htmlFor={`papel-${ticketId}`}>Vínculo</label>
          <select id={`papel-${ticketId}`} className="campo__controle" value={papel} onChange={(e) => setPapel(e.target.value as PapelDaRelacao)}>
            {Object.entries(ROTULO_DO_PAPEL_DA_RELACAO).map(([valor, rotulo]) => (
              <option value={valor} key={valor}>{rotulo}</option>
            ))}
          </select>
        </div>
        <Campo rotulo="Número do chamado" type="number" min={1} value={numero} onChange={(e) => setNumero(e.target.value)} />
        <Botao type="submit" variante="fantasma" disabled={!numero || relacionar.isPending}>Relacionar</Botao>
      </form>

      <div className="flex flex-wrap items-end gap-3">
        <Campo
          rotulo="Mesclar chamado duplicado neste"
          ajuda="O chamado informado vira referência: a conversa e os anexos dele passam a aparecer aqui."
          type="number"
          min={1}
          value={paraMesclar}
          onChange={(e) => {
            setParaMesclar(e.target.value);
            setConfirmando(false);
          }}
        />
        {confirmando ? (
          <>
            <span role="alert">Mesclar o chamado #{paraMesclar} neste? Ele será cancelado e só poderá ser lido.</span>
            <Botao onClick={confirmarMescla} disabled={mesclar.isPending}>Confirmar mescla</Botao>
            <Botao variante="fantasma" onClick={() => setConfirmando(false)}>Cancelar</Botao>
          </>
        ) : (
          <Botao variante="fantasma" disabled={!paraMesclar} onClick={() => setConfirmando(true)}>Mesclar</Botao>
        )}
      </div>
      {falha ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(falha)}</Aviso> : null}
    </Cartao>
  );
}
